import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { saveAs } from 'file-saver';

interface BasesManagerState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    typeTabs: any[];
}

const state: BasesManagerState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    typeTabs: [],
};

export const ITEMS_LIST = 'GET_CITY_LIST';
export const PRODUCT_TYPE_TABS = 'GET_PRODUCT_TYPE_TABS';

// монотонный счётчик загрузок списка (гвард гонки параллельных loadData)
let loadDataSeq = 0;

export const actions: ActionTree<BasesManagerState, RootState> = {
    loadData({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;
        // версия загрузки: быстрая смена вкладки/поиска даёт параллельные
        // запросы — применяем только ответ последнего, иначе медленный
        // старый ответ перезапишет свежий список
        const seq = ++loadDataSeq;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const columns: any = {
                "id": "voc_bases.id",
                "name": "voc_bases.name",
                "copy_name": "voc_bases_copy.name",
                "created_at": "voc_bases.created_at",
                "product_type_id": "voc_bases.product_type_id",
            };

            const filter: any[] = [];

            if (query.name) {
                filter.push({ "$ilike": ["voc_bases.name", {"$": `%${query.name}%`}] })
            }

            // Активная вкладка типа фильтрует список на бэкенде,
            // чтобы пагинация и поиск считались внутри вкладки
            if (query.product_type_id) {
                filter.push({ "$eq": ["voc_bases.product_type_id", {"$": query.product_type_id}] })
            }

            vm.$db([
                vm.$q(ITEMS_LIST)
                .raw('documents.list')
                .action({
                    "columns": columns,
                    "from": "s:voc_bases",
                    "from_as": "voc_bases",
                    joins: [
                      {
                        join: 's:voc_bases',
                        type: 'left',
                        as:   'voc_bases_copy',
                        on: {
                          $eq: ['voc_bases_copy.id', 'voc_bases.copy_id']
                        }
                      },
                    ],
                    page_size: 10,
                    ...({page: parseInt(query.page) || 1}),
                    ...({
                        order: [
                            query.sortDirection == 'desc' ? { $desc: [columns[query.sortField || 'name' ]] } : { $asc: [columns[query.sortField || 'name']] }
                        ]
                    }),
                    ...(
                        filter.length > 1 ?
                        {
                            "where": {
                                "$and": filter
                            }
                        } : {
                            "where": filter[0]
                        }
                    )

                }),
                // Вкладки типов считаются по ВСЕМ базам, а не по текущей странице:
                // join voc_bases → products_types по непустому product_type_id
                // с группировкой по типу (INNER JOIN сам отсекает базы без типа)
                vm.$q(PRODUCT_TYPE_TABS)
                .raw('documents.list')
                .action({
                    "columns": {
                        "id": "pt.id",
                        "name": "pt.name"
                    },
                    "from": "s:voc_bases",
                    "from_as": "vb",
                    joins: [
                      {
                        join: 's:products_types',
                        as:   'pt',
                        on: {
                          $eq: ['pt.id', 'vb.product_type_id']
                        }
                      },
                    ],
                    groups: [
                        'pt.id',
                        'pt.name'
                    ],
                    page_size: 1000,
                    "order": [
                        { "$asc": ["pt.name"] }
                    ]
                })
            ]).then(((result: ServerResult) => {
                // пришёл ответ не последней загрузки — тихо игнорируем
                if (seq !== loadDataSeq) { resolve(); return; }

                const tabsOperation = result.getOperation(PRODUCT_TYPE_TABS);

                commit('SET_ITEMS_LIST', {
                    data: result.getOperation(ITEMS_LIST).getContent(),
                    // Ошибка запроса вкладок не должна ронять список:
                    // мягкая деградация без вкладок
                    typeTabs: tabsOperation.hasError()
                        ? []
                        : (tabsOperation.getContent().documents || [])
                });
                commit('LOADED_SUCCESS');

                resolve();
            }))
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveTodoType({ commit, getters, dispatch }, data) {
        const vm = (this as any)._vm;

        const query = data.id ?
            vm.$q('SAVE_DOCUMENT').update(data.id, data) :
            vm.$q('SAVE_DOCUMENT').create('voc_bases', data);

        return new Promise((resolve, reject) => {
            vm.$db([
                query
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('SAVE_DOCUMENT');

                if (operationResult.hasError()) {
                    reject();
                } else {
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        });
    },

    copyDocument({commit}, {row_id}) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q("BASES_MANAGER_DATA")
                    .raw('documents.get')
                    .action({
                        id: row_id
                    }),
                vm.$q("BASES_PRODUCTS_DATA")
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'vbp.id',
                            base_id: 'vbp.base_id',
                            product_id: 'vbp.product_id'
                        },
                        from: `s:voc_base_products`,
                        from_as: 'vbp',
                        where: {
                            $eq: [`vbp.base_id`, {$: row_id}]
                        }
                    }),
            ]).then((result: ServerResult) => {
                const base = result.getOperation("BASES_MANAGER_DATA").getContent().document;
                base.copy_id = base.id
                delete base.id
                const products = result.getOperation("BASES_PRODUCTS_DATA").getContent().documents.map((item: any) => {
                    return {product_id: item.product_id}
                });

                const saveQuery = vm.$q('BASES_MANAGER_DATA_SAVE')
                    .create('s:voc_bases', base)
                    .withManyRelation('voc_base_products', 'base_id', (createRelated: any) => {
                        return products.map((item: any) => {
                            return createRelated(item);
                        });
                    })
                vm.$db([saveQuery]).then((result: ServerResult) => {
                    const operationResult = result.getOperation('BASES_MANAGER_DATA_SAVE');

                    if (operationResult.hasError()) {
                        console.log('error', operationResult.getContent());

                        commit('LOADED_SUCCESS');
                        reject();
                    } else {
                        commit('ON_FORM_SAVE', operationResult.getContent())

                        commit('LOADED_SUCCESS');
                        resolve(operationResult.getContent());
                    }
                })
                .catch((error: any) => {
                    console.error('errror', error)
                    reject(error);
                });
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    printDocument({ rootState, state }, row_id) {
        const vm = (this as any)._vm;
  
        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('documents.report')
                .action({
                    template: `
<!DOCTYPE html>
<table font=Verdana font-size=10 orientation="portrait" area="A:С" scale="40">
{{#prescript.documents}}
<tr height=30>
  <td wrap border type="date" width=45>{{datetime}}</td>
  <td wrap border width=15></td>
  <td wrap border width=15></td>
</tr>
<tr height=30>
  <td wrap bold border>Потери</td>
  <td wrap border></td>
  <td wrap border align=center color=FF72BF44>1,0%</td>
</tr>
<tr height=30>
  <td wrap bold height=40 size=14 bgcolor=c5e6ad border>Плановый выход Базы {{base_name}} (кг)</td>
  <td wrap border></td>
  <td wrap bold size=14 bgcolor=c5e6ad align=center border>{{total_weight}}</td>
</tr>
<tr><td></td></tr>
<tr height=30>
  <td wrap bold border bgcolor=CFCFCF align=center>Наименование компонентов рецептуры</td>
  <td wrap bold border bgcolor=CFCFCF align=center>Количество компонентов, %</td>
  <td wrap bold border bgcolor=CFCFCF align=center>Количество компонентов, гр</td>
</tr>
{{#ingredinets}}

<tr height=30>
  <td wrap bold border bgcolor={{color}}>{{name}} </td>
  <td wrap bold border bgcolor={{color}} align=center>{{weight}}</td>
  <td wrap bold border bgcolor={{color}} align=center>{{percent}}</td>
</tr>

    {{#ingredinets}}

    <tr height=30>
        <td wrap border>{{name}}
{{subgroup_name}}</td>
        <td wrap border align=center>{{percent}}</td>
        <td wrap border align=center>{{weight}}</td>
    </tr>

    {{/ingredinets}}

{{/ingredinets}}
<tr height=30>
  <td wrap bold border bgcolor=499bca>Вода</td>
  <td wrap bold border bgcolor=499bca align=center></td>
  <td wrap bold border bgcolor=499bca align=center></td>
</tr>
    <tr height=30>
        <td wrap bold border colspan=3 bgcolor=b6d7ea size=14 align=center>Количество воды зависит от стартовой температуры</td>
    </tr>
    {{#water_borders.borders}}
    <tr height=30>
        <td wrap bold border bgcolor=b6d7ea></td>
        <td wrap bold border bgcolor=b6d7ea align=center>{{temp}}</td>
        <td wrap bold border bgcolor=b6d7ea align=center>{{water}}</td>
    </tr>
    {{/water_borders.borders}}
{{/prescript.documents}}
</table>
                `,
                    queries: {
                        prescript: {
                            columns:{
                                id: 'p.id',
                                datetime: 'p.datetime',
                                base_name: 'vb.name',
                                total_weight: 'p.total_weight',
                                water_borders: 'p.water_borders',
                                ingredinets: {
                                    $array_agg: {
                                        $obj: {
                                            name: 'vi.name',
                                            color: 'vi.color',
                                            ingredinets: 'vi.ingredinets'
                                        }
                                    }
                                }
                            },
                            from: 's:prescripts',
                            from_as: 'p',
                            joins: [
                                {
                                  join: 's:voc_bases',
                                  type: 'left',
                                  as:   'vb',
                                  on: {
                                    $eq: ['vb.id', 'p.base_id']
                                  }
                                },
                                {
                                    join: {
                                      columns: {
                                        prescript_id: 'pi.prescript_id',
                                        name: 'vrm.name',
                                        color: 'vrm.color',
                                        ingredinets: {
                                          $array_agg: {
                                              $obj: {
                                                name: 'vi.name',
                                                subgroup_name: {$concat: [{$: '['}, 'vs.name', {$: ']'}]},
                                                weight: 'pi.weight',
                                                percent: 'pi.percent',
                                                position: 'pi.position',
                                              }
                                          }
                                        }
                                      },
                                      from: 's:prescripts_ingredients',
                                      from_as: 'pi',
                                      joins: [
                                        {
                                          join: 's:voc_ingredients',
                                          as:   'vi',
                                          on: {
                                            $eq: ['vi.id', 'pi.ingredient_id']
                                          }
                                        },
                                        {
                                          join: 's:voc_raw_materials',
                                          type: 'left',
                                          as:   'vrm',
                                          on: {
                                            $eq: ['vrm.id', 'vi.raw_materials_id']
                                          }
                                        },
                                        {
                                          join: 's:voc_subgroups',
                                          type: 'left',
                                          as:   'vs',
                                          on: {
                                            $eq: ['vs.id', 'vi.subgroup_id']
                                          }
                                        },

                                      ],
                                      where: {
                                        $not: [{ $eq: ['vs.id', {$: 'd:voc_subgroups:13'}] }]
                                      },
                                      order: [
                                        {$asc: ['pi.position']}
                                      ],
                                      groups: [
                                        'pi.prescript_id',
                                        'pi.position',
                                        'vrm.name',
                                        'vrm.color',
                                      ]
                                    },
                                    type: 'left',
                                    as:   'vi',
                                    on: {
                                      $eq: ['vi.prescript_id', 'p.id']
                                    }
                                },
                            ],
                            where: {
                                $eq: ['p.base_id', {$: row_id}]
                            },
                            groups: [
                                'p.id',
                                'p.datetime',
                                'vb.name',
                                'p.total_weight',
                                'p.water_borders',
                            ]
                        },
                    },
                    format: 'xlsx'
                })
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')
  
                resolve();
            }));
        })
      },

};

export const mutations: MutationTree<BasesManagerState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, {data, typeTabs}) {
        state.documents = data.documents;
        state.typeTabs = typeTabs;

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
};

const getters = {

};

export const STORE_KEY = '$_bases_manger';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
